import React, { useState } from "react";
import { Helmet } from 'react-helmet-async';
import "./TermsPolicy.css";

const faqs = [
  {
    question: "Can I cancel my order after placing it?",
    answer:
      "Orders can be cancelled within 2 minutes of placing them. Post that, our fulfillment process begins, and cancellations may not be accepted. If a cancellation is successful, the full amount will be refunded within 3-5 days.",
  },
  {
    question: "How can I track my order?",
    answer:
      "You can track your order status in real-time from the 'My Orders' section. We’ll also notify you via SMS or app updates about rider assignment, pickup, and delivery progress.",
  },
  {
    question: "Do you deliver to my area?",
    answer:
      "Binzal currently serves selected areas. Enter your location on the homepage to check availability. If your area isn't serviceable yet, we’re working on expanding soon!",
  },
  {
    question: "Are there any delivery charges?",
    answer:
      "We offer free delivery on orders above ₹149. Orders below that may have a nominal delivery charge of ₹15–30 depending on your location and delivery partner availability.",
  },
  {
    question: "What if I receive spoiled or damaged items?",
    answer:
      "If you receive spoiled, broken, or expired products, please raise a complaint within 12 hours along with clear photos or video. After internal verification, refunds or credits will be processed.",
  },
  {
    question: "Which payment methods are accepted?",
    answer:
      "Binzal accepts UPI, debit/credit cards, net banking, and wallet payments. All transactions are processed through secure gateways. We do not accept cash on delivery currently.",
  },
  {
    question: "Is my personal data shared with anyone?",
    answer:
      "No. At Binzal, we never share your personal information with third parties. All data collected is used only for processing and improving your order experience, in compliance with privacy laws.",
  },
  {
    question: "How fast is your delivery service?",
    answer:
      "Most orders are delivered within 30–45 minutes depending on your location and availability. Live tracking is available after rider assignment. Delays may occur during peak hours.",
  },
];

const TermsPolicy = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const toggleFaq = (index) => setOpenIndex(openIndex === index ? null : index);

  return (
    <div className="terms-wrapper">
      {/* ✅ SEO META TAGS */}
      <Helmet>
        <title> Privacy Policy | binzal - Grocery Delivery in Bihar</title>
        <meta name="description" content="Read Binzal’s latest terms, conditions, return policy, privacy rules, and delivery commitments for safe online grocery shopping in Bihar." />
        <meta name="keywords" content="binzal terms and conditions, privacy policy, return policy, delivery info, online grocery Bihar" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Terms & Privacy Policy | binzal" />
        <meta property="og:description" content="Know your rights, refund policy, privacy, and delivery terms while shopping with binzal. Transparent grocery shopping experience." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
      </Helmet>

      <div className="terms-header">
        <h1>Terms & Conditions, Privacy Policy</h1>
        <p className="updated-date">Last updated: June 17, 2025</p>
      </div>

      <div className="policy-grid">
        <div className="policy-box">
          <h4><i className="bi bi-box-arrow-in-down-left"></i> No Return Policy</h4>
          <p>Due to the highly perishable nature of fruits and vegetables, returns are not accepted after delivery is completed. If any damage or spoilage is detected, notify us within 12 hours with proof. After verification, a refund or wallet coupon may be issued.</p>
        </div>

        <div className="policy-box">
          <h4><i className="bi bi-shield-lock"></i> Privacy Commitment</h4>
          <p>Binzal respects your privacy. We collect only essential information (like name, contact number, and address) to fulfill orders. Your data is stored securely using encryption standards and never shared with third-party services without your consent.</p>
        </div>

        <div className="policy-box">
          <h4><i className="bi bi-truck"></i> Delivery & Responsibility</h4>
          <p>Deliveries typically reach you in 30–45 minutes. However, delays may occur due to weather, traffic, or area restrictions. We don’t issue refunds for delay unless it affects the freshness or quality of the items. Thanks for your understanding.</p>
        </div>

        <div className="policy-box">
          <h4><i className="bi bi-cash-coin"></i> Payments & Refunds</h4>
          <p>Binzal accepts online payments only. All transactions are processed through trusted gateways. In case of failed or duplicate payments, your bank will auto-refund the amount within 3–5 business days. Keep your transaction ID for reference.</p>
        </div>

        <div className="policy-box full">
          <h4><i className="bi bi-geo-alt"></i> Service Availability</h4>
          <p>Binzal currently operates in selected urban and semi-urban zones. You can check delivery availability on our homepage. Orders placed outside serviceable areas will be automatically canceled and refunded within 24 hours of placing the order.</p>
        </div>
      </div>

      <div className="faq-wrapper">
        <h3>Frequently Asked Questions</h3>
        <div className="faq-list">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className={`faq-item ${openIndex === index ? "open" : ""}`}
              onClick={() => toggleFaq(index)}
            >
              <div className="faq-question">
                {faq.question}
                <i className={`bi ${openIndex === index ? "bi-dash" : "bi-plus"}`}></i>
              </div>
              {openIndex === index && <p className="faq-answer">{faq.answer}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TermsPolicy;
